import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Account } from './accountType';
import AccountApi from './accountApi';
import { validateEmail, validateLoginId, validatePasswordMatch, validateAndFormatPhoneNumber, validatePassword } from '../lib/validator';
import { UserPlus, Eye, EyeOff, LogIn } from 'lucide-react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import HelpIcon from '../components/ui/helpIcon';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert } from '../components/ui/alert';
import { Link } from '../components/ui/link';

const CreateAccountByInvite: React.FC = () => {
  const [account, setAccount] = useState<Account>({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    company_code: '',
    login_id: '',
    login_password: '',
    auth_provider: '',
    login_password_confirmation: '',
  });

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [companyCode, setcompanyCode] = useState<string>('');
  const [authProvider, setAuthProvider] = useState<string>('');
  const [isCreateSuccess, setIsCreateSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [consentPhone, setConsentPhone] = useState(false);
  const [consentEmail, setConsentEmail] = useState(false);
  const [token, setToken] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const getInviteData = async () => {
      setErrorMessage('');
      const queryParams = new URLSearchParams(location.search);
      const tokenFromUrl = queryParams.get('token');

      if (!tokenFromUrl) {
        setErrorMessage('Invalid or missing invitation token');
        return;
      }
      setToken(tokenFromUrl);

      try {
        const accountInvite = await AccountApi.getAccountInvite(tokenFromUrl);
        setcompanyCode(accountInvite.sender_company_code);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(`Failed to get invitation: ${error.message}`);
        } else {
          setErrorMessage('Failed to get invitation: An unknown error occurred.');
        }
      }
    };
    getInviteData();
  }, [location]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAccount(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];
    setErrorMessage('');

    switch (name) {
      case 'phone': {
        const phoneResult = validateAndFormatPhoneNumber(value);
        if (!phoneResult.isValid) {
          errors.push(phoneResult.error || 'Invalid phone number');
        } else {
          setAccount(prev => ({ ...prev, phone: phoneResult.value }));
        }
        break;
      }
      case 'email': {
        const emailResult = validateEmail(value);
        if (!emailResult.isValid) {
          errors.push(emailResult.error || 'Invalid email address format');
        } else {
          setAccount(prev => ({ ...prev, email: emailResult.value }));
        }
        break;
      }
      case 'login_id': {
        const loginIdResult = validateLoginId(value);
        if (!loginIdResult.isValid) {
          errors.push(loginIdResult.error || 'Invalid login ID');
        } else {
          setAccount(prev => ({ ...prev, login_id: loginIdResult.value }));
        }
        break;
      }
      case 'login_password': {
        const passwordResult = validatePassword(value);
        if (!passwordResult.isValid) {
          errors.push(passwordResult.error || 'Invalid password');
        } else {
          setAccount(prev => ({ ...prev, login_password: passwordResult.value }));
        }
        break;
      }
      case 'login_password_confirmation': {
        const confirmResult = validatePasswordMatch(account.login_password, value);
        if (!confirmResult.isValid) {
          errors.push(confirmResult.error || 'Password not match');
        } else {
          setAccount(prev => ({ ...prev, login_password_confirmation: confirmResult.value }));
        }
        break;
      }
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
    } else {
      setErrorMessage('');
    }
  };

  const validateForm = (): boolean => {
    setErrorMessage('');
    const phoneResult = validateAndFormatPhoneNumber(account.phone);
    if (!phoneResult.isValid) {
      setErrorMessage(phoneResult.error || 'Invalid phone number');
      return false;
    }

    const emailResult = validateEmail(account.email);
    if (!emailResult.isValid) {
      setErrorMessage(emailResult.error || 'Invalid email address');
      return false;
    }

    const loginIdResult = validateLoginId(account.login_id);
    if (!loginIdResult.isValid) {
      setErrorMessage(loginIdResult.error || 'Invalid login Id');
      return false;
    }

    const passwordResult = validatePassword(account.login_password);
    if (!passwordResult.isValid) {
      setErrorMessage(passwordResult.error || 'Invalid password');
      return false;
    }

    const confirmResult = validatePasswordMatch(account.login_password, account.login_password_confirmation);
    if (!confirmResult.isValid) {
      setErrorMessage(confirmResult.error || 'Password not mathch');
      return false;
    }

    if (!termsAccepted) {
      setErrorMessage('You must accept the Terms of Use and Privacy Policy to continue.');
      return false;
    }

    if (!consentPhone) {
      setErrorMessage('You must consent to the use of your phone number for communication.');
      return false;
    }

    if (!consentEmail) {
      setErrorMessage('You must consent to the use of your email for communication.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setErrorMessage('');

    if (!termsAccepted || !consentPhone || !consentEmail) {
      setErrorMessage('You must accept all the terms and consents to continue.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (companyCode.length < 6) {
      setErrorMessage('Your company data is missing or not correct.');
      return;
    }

    try {
      const accountData = { ...account, company_code: companyCode, auth_provider: 'self' };
      const response = await AccountApi.create(accountData);

      const mrjoeData = {
        account_id: response.data.attributes.account_id,
        first_name: response.data.attributes.first_name,
        last_name: response.data.attributes.last_name,
        company_code: companyCode
      };

      const encodedMrjoeData = btoa(JSON.stringify(mrjoeData));
      sessionStorage.setItem(process.env.REACT_APP_SESSION_STORAGE_KEY as string, encodedMrjoeData);
      setAuthProvider("self");
      setIsCreateSuccess(true);
    } catch (error: unknown) {
      setIsCreateSuccess(false);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("An unexpected error occurred.");
      }
    }
  };

  const handleGoogleSuccess = async (response: any) => {
    setErrorMessage('');
    if (!termsAccepted || !consentPhone || !consentEmail) {
      setErrorMessage('You must accept all the terms and consents to continue.');
      return;
    }

    setAuthProvider("google");

    const token = response.credential;
    try {
      const response = await AccountApi.create_google_account(token, companyCode);

      const accountData = {
        account_id: response.data.attributes.account_id,
        first_name: response.data.attributes.first_name,
        last_name: response.data.attributes.last_name,
        company_code: response.data.attributes.company_code,
        company_name: response.data.attributes.company_name
      };

      const encodedMrjoeData = btoa(JSON.stringify(accountData));
      sessionStorage.setItem(process.env.REACT_APP_SESSION_STORAGE_KEY as string, encodedMrjoeData);
      setIsCreateSuccess(true);

      navigate('/main/customer');
    } catch (error) {
      setIsCreateSuccess(false);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  const handleGoogleFailure = () => {
    setErrorMessage('Sign in with Google failed');
    setIsCreateSuccess(false);
  };

  const handleGoLonIn = () => {
    navigate('/login');
  };

  const areAllCheckboxesChecked = termsAccepted && consentPhone && consentEmail;

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID || ''}>
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {!companyCode ? (
          <div>
            {errorMessage && (
              <Alert variant="error" className="my-4">
                {errorMessage}
              </Alert>
            )}
            <p className="text-base text-foreground mt-4">
              If you think that this is an issue, please contact us by clicking Contact Us link at the bottom of the page.
            </p>
          </div>
        ) : (
          <div>
            {errorMessage && (
              <Alert variant="error" className="mb-4">
                {errorMessage}
              </Alert>
            )}

            {isCreateSuccess ? (
              <div>
                {authProvider === "self" && (
                  <div className="mb-4">
                    <p className="text-base text-foreground mb-2">Account has been created.</p>
                    <p className="text-base text-foreground">Click "Go to Log In" button to log in.</p>
                  </div>
                )}
                {authProvider === "google" && (
                  <div className="mb-4">
                    <p className="text-base text-foreground mb-2">Your Google account has been linked.</p>
                    <p className="text-base text-foreground">Please click "Go to Log In" button to log in.</p>
                  </div>
                )}
                <Button onClick={handleGoLonIn} size="medium" variant="default">
                  <LogIn className="size-5" />
                  Go to Log In
                </Button>
                <div className="mb-[400px]"></div>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-foreground text-center mb-6">Create Online Account</h2>

                <p className="text-base font-semibold text-foreground mb-5">
                  There are two options: (1) create your own account, or (2) link your existing Google account.
                </p>

                <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                  <label className="flex items-start gap-2 text-base text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 size-4 cursor-pointer"
                    />
                    <span>
                      I agree to the <Link to="/pub/terms_of_use" variant="primary">Terms of Use</Link> and{' '}
                      <Link to="/pub/privacy_policy" variant="primary">Privacy Policy</Link>
                    </span>
                  </label>

                  <label className="flex items-start gap-2 text-base text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      id="consentPhone"
                      checked={consentPhone}
                      onChange={(e) => setConsentPhone(e.target.checked)}
                      className="mt-1 size-4 cursor-pointer"
                    />
                    <span>
                      I consent to receive SMS messages at the provided phone number for account-related notifications.
                    </span>
                  </label>

                  <label className="flex items-start gap-2 text-base text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      id="consentEmail"
                      checked={consentEmail}
                      onChange={(e) => setConsentEmail(e.target.checked)}
                      className="mt-1 size-4 cursor-pointer"
                    />
                    <span>
                      I consent to receive emails at the provided email address for account-related notifications.
                    </span>
                  </label>
                </div>

                <fieldset className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                  <legend className="text-lg font-semibold text-foreground px-2">
                    Use Your Google Account:
                  </legend>

                  <div className={areAllCheckboxesChecked ? 'opacity-100' : 'opacity-50 pointer-events-none'}>
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleFailure}
                      theme="filled_blue"
                      width="300px"
                    />
                  </div>
                </fieldset>

                  <fieldset className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                  <legend className="text-lg font-semibold text-foreground px-2">
                    Create Your Own Account:
                  </legend>

                  <form onSubmit={handleSubmit} className="space-y-2">
                    <div>
                      <Label htmlFor="first_name" className="text-base">
                        First Name: <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="first_name"
                        type="text"
                        name="first_name"
                        value={account.first_name}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="last_name" className="text-base">
                        Last Name: <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="last_name"
                        type="text"
                        name="last_name"
                        value={account.last_name}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-base">
                        Phone: <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        name="phone"
                        value={account.phone}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-base">
                        Email: <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        name="email"
                        value={account.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <div className="flex items-center gap-1 mb-2">
                        <Label htmlFor="login_id" className="text-base mb-0">
                          Login ID: <span className="text-destructive">*</span>
                        </Label>
                        <HelpIcon message="Login ID must be at least 6 characters long" />
                      </div>
                      <Input
                        id="login_id"
                        type="text"
                        name="login_id"
                        value={account.login_id}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                      />
                    </div>

                    <div>
                      <div className="flex items-center gap-1 mb-2">
                        <Label htmlFor="login_password" className="text-base mb-0">
                          Password: <span className="text-destructive">*</span>
                        </Label>
                        <HelpIcon message="Password must be at least 8 characters long, including both lowercase and uppercase letters, at least one number, and at least one special character" />
                      </div>
                      <div className="relative">
                        <Input
                          id="login_password"
                          type={showPassword ? 'text' : 'password'}
                          name="login_password"
                          value={account.login_password}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-paragraph hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="login_password_confirmation" className="text-base">
                        Password Confirmation: <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="login_password_confirmation"
                        type="password"
                        name="login_password_confirmation"
                        value={account.login_password_confirmation}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Button
                        type="submit"
                        size="medium"
                        variant="default"
                        disabled={!areAllCheckboxesChecked}
                        className={!areAllCheckboxesChecked ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        <UserPlus className="size-5" />
                        Create Account
                      </Button>
                      {!areAllCheckboxesChecked && (
                        <Alert variant='warning'>
                          You must accept all the terms and consents to continue.
                        </Alert>
                      )}
                    </div>
                  </form>
                </fieldset>
              </>
            )}
          </div>
        )}

        <div className="mb-[150px]"></div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default CreateAccountByInvite;